import React, { useEffect, useMemo, useState } from 'react';
import { useIsMobile } from '@guildeducationinc/catalog-tools';
import { trackEvent } from '@src/services/analytics';
import { Select, ContainerTab, ContainerTabs, SelectChangeEvent } from '@guildeducationinc/recess';
import { DISCOVER_AI_TAB, PROGRAM_RECS } from '@src/constants/featureFlags';
import { useDecision } from '@optimizely/react-sdk';
import { useLimitedCatalog } from '@src/hooks/useLimitedCatalog';
import { useRecommendations } from '../Providers/RecommendationsProvider';
import { QuizShelf } from '../QuizShelf';
import { ShelfBannerHeader } from '../ShelfBannerHeader';
import { BookmarksShelf } from '../BookmarksShelf';
import { CoachRecsShelf } from '../CoachRecsShelf';
import { PersonalizedShelf } from '../PersonalizedShelf';
import { DiscoverShelf } from '../DiscoverShelf';
import styles from './ShelfBanner.module.scss';

type TabValues =
	| 'bookmarks'
	| 'coach-recs'
	| 'quiz-results'
	| 'programs-for-you'
	| 'recommendations'
	| 'discover';

interface Tab {
	component: JSX.Element;
	href: string;
	title: string;
	value: TabValues;
}

export const ShelfBanner: React.FC = () => {
	const isMobile = useIsMobile();
	const { recommendationsData } = useRecommendations();
	const { limitedCatalogEnabled } = useLimitedCatalog();
	const [selectedValue, setSelectedValue] = useState<TabValues>('quiz-results');
	const [programRecsDecision] = useDecision(PROGRAM_RECS);
	const [discoverAiTab] = useDecision(DISCOVER_AI_TAB);
	const { layout } = programRecsDecision?.variables || {};
	const showRecs4U = programRecsDecision?.enabled;
	const showDiscoverTab = discoverAiTab?.enabled;

	const defaultTabs: Tab[] = useMemo(
		() => [
			{
				component: <QuizShelf />,
				href: '#quiz-results',
				title: 'Quiz recommendations',
				value: 'quiz-results',
			},
			{
				component: <BookmarksShelf />,
				href: '#bookmarks',
				title: 'Bookmarks',
				value: 'bookmarks',
			},
		],
		[]
	);

	const [tabs, setTabs] = useState<Tab[]>([...defaultTabs]);

	useEffect(() => {
		const programsForYouTab = {
			component: <PersonalizedShelf />,
			href: '#programs-for-you',
			title: 'Programs for you',
			value: 'programs-for-you',
		} as Tab;

		const recommendationsTab = {
			component: <QuizShelf />,
			href: '#quiz-results',
			title: 'Recommendations',
			value: 'recommendations',
		} as Tab;

		const discoverTab = {
			component: <DiscoverShelf />,
			href: '#discover',
			title: 'Discover',
			value: 'discover',
		} as Tab;

		if (showRecs4U) {
			// Add programs-for-you tab between quiz-results and bookmarks in two-tab variant
			if (layout === 'two_tab' && !defaultTabs.some(tab => tab.value === 'programs-for-you')) {
				defaultTabs.splice(1, 0, programsForYouTab);
				setTabs([...defaultTabs]);
			}

			// Replace quiz-results with recommendations in one-tab variant
			if (layout === 'one_tab' && !defaultTabs.some(tab => tab.value === 'recommendations')) {
				defaultTabs.splice(0, 1, recommendationsTab);
				setTabs([...defaultTabs]);
			}
		}
		if (showDiscoverTab) {
			// Add the discover tab at the end, future state it may move it to the first location
			if (!defaultTabs.some(tab => tab.value === 'discover')) {
				defaultTabs.push(discoverTab);
				setTabs([...defaultTabs]);
			}
		}

		if (
			recommendationsData?.coachRecommendations?.length &&
			!defaultTabs.some(tab => tab.value === 'coach-recs')
		) {
			defaultTabs.splice(2, 0, {
				component: <CoachRecsShelf />,
				href: '#coach-recommendations',
				title: 'Coach recommendations',
				value: 'coach-recs',
			});
			setTabs([...defaultTabs]);
		}
	}, [defaultTabs, layout, recommendationsData, showRecs4U, showDiscoverTab]);

	const { hash } = window.location;

	useEffect(() => {
		if (hash) {
			const tab = tabs.find(tab => tab.href === hash);
			if (tab) {
				setSelectedValue(tab.value);
			}
		} else {
			return window.history.replaceState(null, '', '#quiz-results');
		}
	}, [hash, tabs, layout]);

	const selectOptions = tabs.map(tab => ({
		label: tab.title,
		value: tab.value,
	}));

	const handleChange = (
		event: SelectChangeEvent<
			{
				label: string;
				value: TabValues;
			},
			false
		>
	) => {
		const selectedTab = tabs.find(tab => tab.value === event.selection.value);
		setSelectedValue(event.selection.value);
		window.history.replaceState(null, selectedTab.title, selectedTab.href);
		trackEvent('Clickable', {
			loggedIn: true,
			product: 'catalog',
			tag: 'catalog_shelf',
			text: selectedTab.value,
			type: 'tab',
		});
	};

	const handleOnActive = (index: number) => {
		const selectedTab = tabs[index];
		setSelectedValue(selectedTab.value);
		window.history.replaceState(null, selectedTab.title, selectedTab.href);
		trackEvent('Clickable', {
			loggedIn: true,
			product: 'catalog',
			tag: 'catalog_shelf',
			text: selectedTab.value,
			type: 'tab',
		});
	};

	if (limitedCatalogEnabled) {
		return (
			<div className={styles.bannerContainer}>
				<ShelfBannerHeader />
			</div>
		);
	}

	return (
		<div className={styles.bannerContainer}>
			<ShelfBannerHeader />
			{isMobile ? (
				<>
					<Select
						className={styles.select}
						hideLabel
						isClearable={false}
						label='Shelf select'
						onChange={event => handleChange(event)}
						options={selectOptions}
						value={selectedValue}
					/>
					{tabs.find(tab => tab.value === selectedValue)?.component}
				</>
			) : (
				<ContainerTabs
					activeIndex={tabs.findIndex(tab => tab.value === selectedValue)}
					border={false}
					className={styles.tabNavigation}
					label='Shelf tabs'
					onActive={handleOnActive}
				>
					{tabs.map(tab => (
						<ContainerTab aria-label={tab.title} key={tab.value} title={tab.title}>
							{tab.component}
						</ContainerTab>
					))}
				</ContainerTabs>
			)}
		</div>
	);
};
